/** QAConfirmCardsSlot — one ConfirmCard per booking tab. Cards, rows and
 *  labels follow the tenant's tab / slot / field visibility (ctx.visibility). */
import { ConfirmCard } from "../_shared";
import { ALL_VISIBLE } from "@/lib/formVisibility";

const dash = (v) => (v === undefined || v === null || v === "" ? "—" : v);

// Each row is sourced from a registry field (f) or a slot (s).
export function cardsFor(values) {
  const v = values;
  return [
    { tab: "schedule", title: "Schedule", rows: [
      { s: "schedule.header", label: "Direction", value: v.direction },
      { s: "schedule.header", label: "Start", value: v.scheduled_start ? new Date(v.scheduled_start).toLocaleString("en-AU", { timeZone: v._booking_tz || undefined }) : "" },
      { s: "schedule.header", label: "End",   value: v.scheduled_end   ? new Date(v.scheduled_end).toLocaleString("en-AU", { timeZone: v._booking_tz || undefined })   : "" },
      { f: "external_ref", label: "External ref", value: v.external_ref },
      { f: "internal_ref", label: "Internal ref", value: v.internal_ref },
    ] },
    { tab: "vehicle", title: "Vehicle", rows: [
      { s: "vehicle.picklists", label: "Type", value: v.vehicle_type },
      { f: "rego_prime_mover", label: "Prime rego", value: v.rego_prime_mover },
      { f: "rego_trailer_1",   label: "Trailer 1",  value: v.rego_trailer_1 },
      { f: "rego_trailer_2",   label: "Trailer 2",  value: v.rego_trailer_2 },
      { f: "gvm_kg",           label: "GVM (kg)",   value: v.gvm_kg },
    ] },
    { tab: "driver", title: "Driver", rows: [
      { s: "driver.picker", label: "Licence type",   value: v.driver_licence_type },
      { s: "driver.picker", label: "Licence number", value: v.driver_licence_number },
      { s: "driver.picker", label: "Licence state",  value: v.driver_licence_state },
    ] },
    { tab: "goods", title: "Goods", rows: [
      { f: ["sender_company", "sender_name"], label: "Sender",
        value: [v.sender_company, v.sender_name].filter(Boolean).join(" — ") },
      { f: ["receiver_company", "receiver_name"], label: "Receiver",
        value: [v.receiver_company, v.receiver_name].filter(Boolean).join(" — ") },
      { f: "goods_description", label: "Description", value: v.goods_description },
      { f: "weight", label: "Weight", value: v.weight ? `${v.weight} t` : "" },
    ] },
    { tab: "restraint", title: "Restraint + Pallets", rows: [
      { s: "restraint.picklists", label: "Method", value: v.restraint_method },
      { f: "straps_count",        label: "Straps", value: v.straps_count },
      { f: "chains_count",        label: "Chains", value: v.chains_count },
      { f: "boards_sticks_count", label: "Boards/sticks", value: v.boards_sticks_count },
      { f: "other_restraint_type", label: "Other",
        value: v.other_restraint_type ? `${v.other_restraint_type} (${v.other_restraint_count || 0})` : "" },
      { f: "pallets_in",  label: "Pallets IN",  value: v.pallets_in },
      { f: "pallets_out", label: "Pallets OUT", value: v.pallets_out },
      { s: "restraint.picklists", label: "Pallet brand", value: v.pallet_type },
      { s: "restraint.picklists", label: "Load rule",    value: v.load_rule },
    ] },
    { tab: "compliance", title: "Compliance", rows: [
      { s: "compliance.cold-chain", label: "Cold chain", value: v.cold_chain },
      { f: "food_safety",     label: "Food safety", value: (v.food_safety || []).join(", ") },
      { f: "dangerous_goods", label: "DG classes",  value: (v.dangerous_goods || []).join(", ") },
    ] },
  ];
}

export default function QAConfirmCardsSlot({ values, set, ctx }) {
  const vis = ctx?.visibility || ALL_VISIBLE;
  const flip = (tabKey) => (val) =>
    set("qa_confirmations", { ...values.qa_confirmations, [tabKey]: val });

  const rowVisible = (r) => (r.s ? vis.slotVisible(r.s)
    : [].concat(r.f).some((k) => vis.fieldVisible(k)));
  const rowLabel = (r) => (typeof r.f === "string" ? vis.fieldLabel(r.f, r.label) : r.label);

  const cards = cardsFor(values)
    .filter((c) => vis.tabVisible(c.tab))
    .map((c) => ({ ...c, rows: c.rows.filter(rowVisible) }))
    .filter((c) => c.rows.length > 0);

  return (
    <>
      <div className="text-xs text-slate-500 -mt-1">
        Forklift driver reviews each tab below. <strong>Confirm</strong>{" "}
        once the load matches what the booker entered, or <strong>Flag NCR</strong>{" "}
        from the bookings list if it doesn&apos;t.
      </div>
      {cards.map((c) => (
        <ConfirmCard key={c.tab} tab={c.tab} title={vis.tabLabel(c.tab, c.title)}
          confirmations={values.qa_confirmations}
          onConfirm={flip(c.tab)}
          rows={c.rows.map((r) => [rowLabel(r), dash(r.value)])}
        />
      ))}
    </>
  );
}
